import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from '@phosphor-icons/react';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_PRIMARY } from '../buttonStyles';
import { CoachPhoto } from '../liveBooking/CoachHeader';
import { OfferSheet } from '../CallRecommend';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { subscribeNextLessonWith } from '../../services/liveV2';
import { subscribeRecommendations, type LiveRecommendation } from '../../services/liveLessons';
import { timeLabel } from '../../utils/liveCalendar';
import { leftAtOf, personState } from '../../utils/roomPresence';
import { afterCallTarget, doneForToday, lessonKind, callMinutes, joinIsNews, lessonsPath, noShowInCall, reviewQuestion } from '../../utils/callEnd';
import { isPlayerNoShow, playerNoShowStatus } from '../../utils/playerNoShow';
import { NoShowDecision } from './NoShowDecision';
import { useIsMobile } from '../../contexts/ResponsiveContext';
import { liveLessonTitle, liveTimeLine } from '../../utils/liveLessonText';
import { useViewerZone } from '../../hooks/useViewerZone';
import { createConversation } from '../../services/chat';
import { useRoomPresence } from '../../hooks/useRoomPresence';
import { OFFER_TITLE } from '../../utils/recommendOffers';
import { pairRoomId } from '../../utils/liveRoom';
import { doneWithRating, flushLessonNotes, keptRatings } from '../../services/lessonRating';
import { lessonRated, roomHidesRating } from '../../utils/lessonRated';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { LiveCallBooking } from '../../types/liveCall';

/**
 * The room's screens around the call (redesign spec, batch 5), on the call
 * canvas. None is a dead end: each has a way on.
 */

const SAFE_AREA = {
  paddingTop: 'env(safe-area-inset-top)',
  paddingRight: 'env(safe-area-inset-right)',
  paddingBottom: 'env(safe-area-inset-bottom)',
  paddingLeft: 'env(safe-area-inset-left)',
} as const;

/** The secondary on the call canvas: white at 14, as the bar's controls. */
export const BTN_ON_CANVAS =
  'h-12 rounded-[100px] inline-flex items-center justify-center gap-[6px] px-6 bg-white/[0.14] text-white ' +
  'text-[17px] leading-[22px] font-medium tracking-[-0.43px] cursor-pointer ios-press disabled:opacity-40 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40';

export function RoomShell({ title, line, children }: { title: string; line?: string; children?: ReactNode }) {
  return (
    <div className="fixed inset-0 overflow-y-auto bg-call-canvas text-white" style={SAFE_AREA}>
      <div className="mx-auto flex min-h-full w-full max-w-[420px] flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <h1 className="text-[22px] leading-[28px] font-semibold">{title}</h1>
        {line && <p className="text-[15px] leading-[20px] text-white/65">{line}</p>}
        {children && <div className="mt-4 flex w-full flex-col gap-3">{children}</div>}
      </div>
    </div>
  );
}

/** On the way into a room: the spinner on the call canvas, and what is happening. */
export function RoomLoading({ line }: { line?: string }) {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-call-canvas text-white" style={SAFE_AREA}
      role="status" aria-label={line ?? 'Loading'}>
      <ButtonSpinner size={28} />
      {line && <p className="text-[15px] leading-[20px] text-white/65">{line}</p>}
    </div>
  );
}

function ToLessons({ primary = false }: { primary?: boolean }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  return (
    <button type="button" onClick={() => navigate(lessonsPath(userProfile?.currentMode))} className={primary ? `${BTN_PRIMARY} w-full` : `${BTN_ON_CANVAS} w-full`}>
      Go to Lessons
    </button>
  );
}

/** Before the window opens: "Starts in 25 min". */
export function StartsInScreen({ booking, tz }: { booking: LiveCallBooking; tz: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const mins = Math.max(1, Math.ceil((new Date(booking.scheduledStartAt).getTime() - now) / 60_000));
  const title = mins < 60 ? `Starts in ${mins} min`
    : `Starts at ${timeLabel(booking.scheduledStartAt, tz)}`;
  return (
    <RoomShell title={title} line="The call opens 10 minutes early.">
      <ToLessons primary />
    </RoomShell>
  );
}

/**
 * After this side left: "Done for today?" (decision 54), for both roles,
 * with what the other person is doing, from their presence (never their
 * tracks), and the ways on (utils/callEnd doneForToday): Rejoin while they
 * are on the call; Rejoin, Message and Back to Lessons while they have not
 * joined, told the moment they do; the clock's no-show line with Done at 10
 * minutes after the start; Done and Rejoin once they have been and gone.
 * Done is this person's confirmation that the lesson happened (55).
 */
export function DoneForTodayScreen({ booking, isCoach, otherFirst, otherId, roomId, onRejoin, onDone }: {
  booking: LiveCallBooking; isCoach: boolean; otherFirst: string; otherId?: string; roomId: string;
  onRejoin: () => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { showInfo, showError } = useToast();
  const zone = useViewerZone();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);
  const theirs = useRoomPresence({ bookingId: booking.id, roomId, uid: otherId, side: isCoach ? 'player' : 'coach', booking });
  const theirState = personState(theirs, now);
  const leftIso = theirState === 'left' ? leftAtOf(theirs) : null;
  const view = doneForToday({
    theirState, otherFirst, isCoach, kind: lessonKind(booking), startIso: booking.scheduledStartAt, nowMs: now,
    leftTime: leftIso ? timeLabel(leftIso, zone) : null,
  });
  // "We'll tell you the moment they do": their join, told here as it lands,
  // never one from before this screen opened.
  const [since] = useState(() => Date.now());
  const told = useRef(false);
  const joinedAt = theirs?.joinedAt;
  useEffect(() => {
    if (told.current || !joinIsNews({ theirState, joinedAt, sinceMs: since })) return;
    told.current = true;
    showInfo(`${otherFirst} joined the call`);
  }, [theirState, joinedAt, since, otherFirst, showInfo]);
  const [messaging, setMessaging] = useState(false);
  const message = async () => {
    if (!currentUser || !otherId) return;
    setMessaging(true);
    try { navigate(`/inbox/${await createConversation(currentUser.uid, otherId)}`); }
    catch { showError('Could not open the chat. Please try again.'); setMessaging(false); }
  };
  // A player no-show the server holds (154, 13): its line, and the coach's decision while pending.
  const held = isPlayerNoShow(booking) ? playerNoShowStatus(booking, isCoach, zone) : null;
  const inCall = theirState === 'camera_on' || theirState === 'camera_off' || theirState === 'camera_paused';
  const noShow = noShowInCall({ isCoach, theyAreIn: inCall, clockLine: null, held });
  const line = noShow?.line ?? view.line;
  const label = { done: 'Done', rejoin: 'Rejoin', message: `Message ${otherFirst}`, lessons: 'Back to Lessons' } as const;
  const act = { done: onDone, rejoin: onRejoin, message: () => { void message(); }, lessons: () => navigate(lessonsPath(userProfile?.currentMode)) } as const;
  return (
    <RoomShell title={view.title} line={theirs || noShow ? line : undefined}>
      {noShow?.decide && <NoShowDecision bookingId={booking.id} playerFirst={otherFirst} member={!!booking.membershipId} />}
      {view.actions.map((a, i) => (
        <button key={a} type="button" onClick={act[a]} disabled={a === 'message' && messaging} aria-busy={a === 'message' && messaging}
          className={`${i === 0 && !noShow?.decide ? BTN_PRIMARY : BTN_ON_CANVAS} w-full`} data-done-action={a}>
          {a === 'message' && messaging ? <ButtonSpinner /> : label[a]}
        </button>
      ))}
    </RoomShell>
  );
}

/**
 * Rule 9: a call in the pair's room outside every lesson, waiting for the
 * other side to accept it from the thread. Not a lesson, not recorded.
 */
export function WaitingToAcceptScreen({ other, onLeave }: { other: string; onLeave: () => void }) {
  return (
    <RoomShell title={`Waiting for ${other} to accept`} line="It isn't a lesson and isn't recorded.">
      {/* Leave is red in every state of the call (165; Ryan, 4 Nov). */}
      <button type="button" onClick={onLeave} className={`${BTN_ON_CANVAS.replace('bg-white/[0.14]', 'bg-danger')} w-full`} data-leave>Leave</button>
    </RoomShell>
  );
}

/** A lesson cancelled, or one that did not happen. */
export function NotOnScreen({ line }: { line: string }) {
  return (
    <RoomShell title={line}>
      <ToLessons primary />
    </RoomShell>
  );
}

/**
 * The player's rating on Call ended (decisions 105, 134): "How was the
 * lesson?" and the stars; the review the coach sees opens once a star is
 * tapped. Nothing here sends: the screen's Done saves both, Skip leaves the
 * review out.
 */
function PlayerReview({ coachFirst, stars, onStars, review, onReview }: {
  coachFirst: string; stars: number; onStars: (n: number) => void; review: string; onReview: (t: string) => void;
}) {
  return (
    <div className="mt-2 flex w-full flex-col items-center gap-2" data-call-rating>
      <p className="text-[17px] leading-[22px] font-semibold text-white">How was the lesson?</p>
      <div role="radiogroup" aria-label="Rate the lesson" className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={stars === n} aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
            onClick={() => onStars(n)}
            className="ios-press flex h-11 w-11 cursor-pointer items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
            <Star size={28} weight={n <= stars ? 'fill' : 'regular'} className={n <= stars ? 'text-[#FACC15]' : 'text-white/60'} aria-hidden="true" />
          </button>
        ))}
      </div>
      {stars > 0 && <div className="mt-2 w-full text-left" data-rating-review>
        <label htmlFor="lesson-review" className="block text-[15px] leading-[20px] text-white">{reviewQuestion(coachFirst)}</label>
        <textarea id="lesson-review" value={review} onChange={(e) => onReview(e.target.value)} rows={3} maxLength={2000}
          className="mt-2 block w-full resize-none rounded-[12px] bg-white/[0.14] px-4 py-3 text-[17px] leading-[22px] text-white placeholder:text-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40" />
      </div>}
    </div>
  );
}

/**
 * Call ended: who it was with, when and how long, and Done, which opens
 * Lessons. The player's asks "How was the lesson?" with the stars; a tap
 * on one opens "Would you like to leave a quick review for Ryan?
 * (optional)" (134). Done, the primary, saves both at once and goes, no
 * spinner (105): the lesson is Delivered on it. Skip, the quiet secondary,
 * does the same without the review: it skips the review only. The coach's is this with
 * Done only, once the player has rated (106). After a Free Intro Call with
 * nothing booked, the coach's recommendation with See Offer.
 */
export function CallEndedScreen({ booking, isCoach, guest = false }: { booking: LiveCallBooking; isCoach: boolean; guest?: boolean }) {
  const navigate = useNavigate();
  const narrow = useIsMobile();
  const { currentUser, userProfile } = useAuth();
  const zone = useViewerZone();
  const coach = useUserIdentity(booking.coachId, 'coach');
  const other = useUserIdentity(isCoach ? booking.playerId : booking.coachId, isCoach ? 'player' : 'coach');
  const otherName = other?.name ?? (isCoach ? booking.playerName : booking.coachName) ?? '';
  const [recs, setRecs] = useState<LiveRecommendation[]>([]);
  const [nextBooked, setNextBooked] = useState<boolean | null>(null);
  const [offer, setOffer] = useState<LiveRecommendation | null>(null);
  const isIntro = booking.offeringType === 'free_intro_call';
  useEffect(() => {
    if (!currentUser || isCoach || !isIntro) return;
    const a = subscribeRecommendations(booking.id, currentUser.uid, 'player', setRecs);
    const b = subscribeNextLessonWith(currentUser.uid, booking.coachId,
      (n) => setNextBooked(!!n && n.id !== booking.id));
    return () => { a(); b(); };
  }, [currentUser, isCoach, isIntro, booking.id, booking.coachId]);

  const rec = isIntro && nextBooked === false ? recs.find((r) => r.status === 'sent') : undefined;
  const coachFirst = (coach?.name ?? booking.coachName ?? '').split(' ')[0] || 'Your coach';

  // The player's rating. Asked once per lesson (finding 17): not when the
  // booking, this device or the room says it is rated already.
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState('');
  useEffect(() => { if (!isCoach) void flushLessonNotes(); }, [isCoach]);
  const [ratedBefore, setRatedBefore] = useState(() => lessonRated({ booking, kept: keptRatings() }));
  const uid = currentUser?.uid;
  const tapped = useRef(0);
  tapped.current = stars;
  useEffect(() => {
    if (!uid || isCoach) return;
    let live = true;
    getDoc(doc(db, 'webrtc_rooms', pairRoomId(booking)))
      .then((snap) => {
        const roomRating = (snap.data()?.ratings as Record<string, { stars?: number; bookingId?: string }> | undefined)?.[uid];
        if (live && roomHidesRating(roomRating, booking.id, tapped.current)) setRatedBefore(true);
      })
      .catch(() => { /* the stars stay */ });
    return () => { live = false; };
  // Decided once, as the screen opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, booking.id, isCoach]);
  const asking = !isCoach && !ratedBefore;
  // One Done: the player's confirmation with the stars and the review, sent
  // without waiting (105), and on at once to that lesson's Delivered page, for both (146).
  const finish = (withReview = true) => {
    if (asking) void doneWithRating(booking.id, stars, withReview ? review : '');
    const t = afterCallTarget({ isCoach, guest, bookingId: booking.id, narrow, currentMode: userProfile?.currentMode });
    navigate(t.to, t.state ? { state: t.state } : undefined);
  };

  return (
    <RoomShell title="Call ended">
      <div className="flex flex-col items-center gap-1 pb-2" data-call-ended-who>
        <CoachPhoto url={other?.avatarUrl} name={otherName} size={72} />
        <p className="mt-2 text-[17px] leading-[22px] font-semibold">{otherName}</p>
        <p className="text-[15px] leading-[20px] text-white/65">{liveLessonTitle(booking)}</p>
        <p className="text-[15px] leading-[20px] text-white/65">{liveTimeLine(booking.scheduledStartAt, callMinutes(booking), zone)}</p>
      </div>
      {asking && <PlayerReview coachFirst={coachFirst} stars={stars} onStars={setStars} review={review} onReview={setReview} />}
      {rec && (
        <div className="mb-2 rounded-[16px] bg-call-tile p-4 text-left">
          <div className="flex items-center gap-3">
            <CoachPhoto url={coach?.avatarUrl} name={coach?.name ?? booking.coachName} size={44} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] leading-[18px] text-white/65">{coachFirst} recommends</div>
              <div className="text-[17px] leading-[22px] font-semibold">{OFFER_TITLE[rec.offer]}</div>
            </div>
          </div>
          {rec.note?.trim() && <p className="mt-3 text-[15px] leading-[20px]">“{rec.note.trim()}”</p>}
          <button type="button" onClick={() => setOffer(rec)} className={`${BTN_PRIMARY} mt-4 w-full`}>See Offer</button>
        </div>
      )}
      {/* Done opens the lesson's Delivered page, for both (146). */}
      <button type="button" onClick={() => finish()} className={`${BTN_PRIMARY} w-full`} data-call-ended-done>Done</button>
      {/* Skip: the quiet secondary, the review left out; the lesson still finishes (134). */}
      {asking && (
        <button type="button" onClick={() => finish(false)} data-call-ended-skip
          className="ios-press h-11 rounded-[100px] px-6 text-[17px] leading-[22px] font-medium text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
          Skip
        </button>
      )}
      {offer && (
        <OfferSheet bookingId={booking.id} recommendation={offer} coachName={coach?.name ?? booking.coachName}
          coachAvatarUrl={coach?.avatarUrl ?? undefined} onPaid={() => {}} onClose={() => setOffer(null)} />
      )}
    </RoomShell>
  );
}
